import { useEffect, useRef } from 'react'
import { Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'

interface GPSMapProps {
  /** Where the address resolves to — static pin. */
  geocodedLat?: number
  geocodedLng?: number
  /** Admin-set validation center — draggable when onCenterChange provided. */
  centerLat?: number
  centerLng?: number
  /** GPS acceptance radius drawn around the validation center. */
  radiusMetres: number
  /** Employee submission location. */
  reportLat?: number
  reportLng?: number
  /** When provided, the validation center pin becomes draggable. */
  onCenterChange?: (lat: number, lng: number) => void
  /** GPS status from the report — controls employee pin colour. */
  reportGPSStatus?: string
}

const DEFAULT_ZOOM = 15

function getMapCenter(
  centerLat?: number,
  centerLng?: number,
  geocodedLat?: number,
  geocodedLng?: number,
): google.maps.LatLngLiteral | undefined {
  if (centerLat != null && centerLng != null) return { lat: centerLat, lng: centerLng }
  if (geocodedLat != null && geocodedLng != null) return { lat: geocodedLat, lng: geocodedLng }
  return undefined
}

function MapCenterUpdater({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap()
  useEffect(() => {
    map?.panTo({ lat, lng })
  }, [map, lat, lng])
  return null
}

function RadiusCircle({
  center,
  radiusMetres,
}: {
  center: google.maps.LatLngLiteral
  radiusMetres: number
}) {
  const map = useMap()
  const mapsLib = useMapsLibrary('maps')
  const circleRef = useRef<google.maps.Circle | null>(null)

  useEffect(() => {
    if (!map || !mapsLib) return
    circleRef.current = new mapsLib.Circle({
      map,
      center,
      radius: radiusMetres,
      fillColor: '#3b82f6',
      fillOpacity: 0.1,
      strokeColor: '#3b82f6',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      clickable: false,
    })
    return () => {
      circleRef.current?.setMap(null)
      circleRef.current = null
    }
  }, [map, mapsLib]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    circleRef.current?.setCenter(center)
  }, [center.lat, center.lng]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    circleRef.current?.setRadius(radiusMetres)
  }, [radiusMetres])

  return null
}

export function GPSMap({
  geocodedLat,
  geocodedLng,
  centerLat,
  centerLng,
  radiusMetres,
  reportLat,
  reportLng,
  onCenterChange,
  reportGPSStatus,
}: GPSMapProps) {
  const mapCenter = getMapCenter(centerLat, centerLng, geocodedLat, geocodedLng)
  if (!mapCenter) return null

  const effectiveCenterLat = centerLat ?? geocodedLat!
  const effectiveCenterLng = centerLng ?? geocodedLng!
  const circleCenter = { lat: effectiveCenterLat, lng: effectiveCenterLng }

  return (
    <Map
      defaultCenter={mapCenter}
      defaultZoom={DEFAULT_ZOOM}
      mapId="gps-map"
      className="w-full h-64 rounded-md"
      gestureHandling="cooperative"
      disableDefaultUI={false}
    >
      {geocodedLat != null && geocodedLng != null && (
        <MapCenterUpdater lat={geocodedLat} lng={geocodedLng} />
      )}
      <RadiusCircle center={circleCenter} radiusMetres={radiusMetres} />

      {geocodedLat != null && geocodedLng != null && (
        <AdvancedMarker
          position={{ lat: geocodedLat, lng: geocodedLng }}
          title="Building address"
        >
          <div className="w-4 h-4 rounded-full bg-gray-500 border-2 border-white shadow" />
        </AdvancedMarker>
      )}

      <AdvancedMarker
        position={{ lat: effectiveCenterLat, lng: effectiveCenterLng }}
        draggable={!!onCenterChange}
        title={onCenterChange ? 'Drag to set GPS validation center' : 'GPS validation center'}
        onDragEnd={(e) => {
          if (onCenterChange && e.latLng) {
            onCenterChange(e.latLng.lat(), e.latLng.lng())
          }
        }}
      >
        <div className="w-5 h-5 rounded-full bg-blue-500 border-2 border-white shadow-md" />
      </AdvancedMarker>

      {reportLat != null && reportLng != null && (
        <AdvancedMarker
          position={{ lat: reportLat, lng: reportLng }}
          title="GPS location at time of submission"
        >
          <div className="relative flex items-center justify-center">
            {/* Pulse ring */}
            <div className={`absolute w-10 h-10 rounded-full opacity-30 animate-ping ${
              reportGPSStatus === 'outside_range' ? 'bg-amber-400' : 'bg-green-400'
            }`} />
            {/* Pin body — teardrop shape */}
            <div className={`relative flex items-center justify-center w-8 h-8 rounded-full rounded-bl-none rotate-[-45deg] border-2 border-white shadow-lg ${
              reportGPSStatus === 'outside_range' ? 'bg-amber-500' : 'bg-green-500'
            }`}>
              {/* Person icon, counter-rotated so it stays upright */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="white"
                className="w-4 h-4 rotate-[45deg]"
              >
                <circle cx="12" cy="7" r="3" />
                <path d="M12 12c-4 0-6 2-6 4v1h12v-1c0-2-2-4-6-4z" />
              </svg>
            </div>
          </div>
        </AdvancedMarker>
      )}
    </Map>
  )
}
